import { useState } from "react";
import { Eraser, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Admin-only: wipe every reading for one meter on one shed (cancelling any invoices they're on)
export const ClearMeterButton = ({ unitId, type, label, onCleared }) => {
  const { user } = useAuth();
  const [scope, setScope] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  if (user?.role !== "admin") return null;

  const open = async () => {
    try { setScope(await api.adminClearPreview(unitId, type)); setTyped(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load readings"); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.adminClearReadings(unitId, type, typed);
      toast.success(`Cleared ${r.deleted} ${label.toLowerCase()} reading(s)${r.cancelled.length ? ` and cancelled ${r.cancelled.join(", ")}` : ""}`);
      setScope(null);
      onCleared?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not clear readings"); } finally { setBusy(false); }
  };

  const matches = scope && typed.trim().toUpperCase() === scope.unit_number.toUpperCase();
  return (
    <>
      <button onClick={open} data-testid={`${type}-clear-all-btn`} className="ml-2 inline-flex items-center gap-1 rounded border border-red-200 px-1.5 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-red-50">
        <Eraser className="h-3 w-3" /> Clear all readings
      </button>
      <Dialog open={!!scope} onOpenChange={(o) => !o && setScope(null)}>
        <DialogContent className="max-w-md" data-testid={`${type}-clear-dialog`}>
          <DialogHeader>
            <DialogTitle className="text-red-700">Clear all {label} readings?</DialogTitle>
            <DialogDescription>This can't be undone. The next reading you enter becomes the meter's new starting point.</DialogDescription>
          </DialogHeader>
          {scope && (
            <div className="space-y-3 text-sm">
              <p data-testid={`${type}-clear-count`}><b>{scope.count}</b> {label.toLowerCase()} reading(s) on <b>{scope.unit_number}</b> will be deleted.</p>
              {scope.invoices.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-800" data-testid={`${type}-clear-invoices`}>
                  These invoices will also be cancelled: {scope.invoices.map((i) => `${i.invoice_number} ($${i.total.toFixed(2)})`).join(", ")}. Other readings on them are unlocked.
                </div>
              )}
              <div className="space-y-1">
                <p className="text-slate-600">Type <b>{scope.unit_number}</b> to confirm:</p>
                <Input data-testid={`${type}-clear-confirm-input`} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={scope.unit_number} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setScope(null)}>Cancel</Button>
            <Button data-testid={`${type}-clear-confirm-btn`} onClick={confirm} disabled={!matches || busy} className="bg-red-600 hover:bg-red-700">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Clear readings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
